import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import MessagingPanel from "./MessagingPanel";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/**
 * ===========================================================================
 * LOCATIONS — shelves and totes
 * ===========================================================================
 * Two kinds of thing live in this table and the screen is built around telling
 * them apart, because confusing them is what goes wrong in the depot:
 *
 *   BRANCH LOCATIONS   Shelves and bays inside the building. Bags wait here
 *                      for whoever comes to fetch them. Everyone at the branch
 *                      sees them.
 *
 *   DRIVER TOTES       Belong to one driver. Only he sees them, only he loads
 *                      into them, and one of them is his default - the box new
 *                      bags go into when he does not pick another.
 *
 * SO THE SCREEN IS GROUPED BY OWNER, not one flat table with an owner column.
 * The question an operator actually arrives with is "what has Dave got" or
 * "what shelves have we got", and a sortable column answers neither without
 * being sorted first.
 *
 * DRIVERS ARE A LIST BESIDE THEIR TOTES, not a stack of panels. A depot with
 * ten drivers stacked vertically is a page nobody scrolls: you cannot see the
 * roster and one driver's boxes at the same time, which is exactly the
 * comparison being made. One driver is selected at a time and their totes fill
 * the panel to the right.
 *
 * THE DEFAULT IS SHOWN ON THE DRIVER, NOT THE LOCATION. It is stored per driver
 * per branch on user_accounts, so a driver at two depots has a different
 * default at each - which a badge on a location could not express. Setting it
 * here writes the same field the driver's own app writes, and last write wins:
 * a driver who has physically moved to another tote knows something the office
 * does not.
 *
 * RETIRE, NEVER DELETE. Deliveries point at locations, and a shelf thrown out
 * last year still has to make sense in the history of the parcels that sat on
 * it. The server refuses to retire anything with parcels still waiting.
 */

const S = {
  wrap:   { padding: 16, fontFamily: "system-ui, sans-serif", color: "#111827" },
  h1:     { fontSize: 18, fontWeight: 700, margin: 0 },
  panel:  { border: "1px solid #d1d5db", borderRadius: 10, background: "#fcfcfd",
            padding: "12px 14px", marginBottom: 16 },
  label:  { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
            textTransform: "uppercase", letterSpacing: 0.5 },
  input:  { padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db",
            borderRadius: 4, width: "100%", boxSizing: "border-box" },
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb",
            fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
            color: "#6b7280", whiteSpace: "nowrap" },
  td:     { padding: "6px 8px", borderBottom: "1px solid #f3f4f6",
            verticalAlign: "top" },
  muted:  { color: "#6b7280", fontSize: 11 },
  btn:    { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db",
            borderRadius: 4, padding: "5px 10px", fontSize: 12.5, cursor: "pointer" },
  btnGo:  { background: "#0f766e", color: "#fff", border: "1px solid #0f766e",
            borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer" },
  error:  { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
            padding: 10, borderRadius: 6, fontSize: 13 },
  chip:   { border: "1px solid #0f766e", background: "#ccfbf1", color: "#0f4c47",
            borderRadius: 12, padding: "1px 9px", fontSize: 11, fontWeight: 700,
            whiteSpace: "nowrap" },
};

const TYPES = [
  { value: "shelf", label: "Shelf" },
  { value: "bay",   label: "Bay" },
  { value: "tote",  label: "Tote" },
  { value: "van",   label: "Van" },
  { value: "other", label: "Other" },
];

async function api(path, token, options = {}) {
  const res = await fetch(`${API_BASE}/api/locations${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message)
      || `Request failed (${res.status})`);
  }
  return data;
}

export default function Locations({ token }) {
  const masked = useMaskNames();

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [locations, setLocations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(null);   // null | { driverId | "" }
  const [editing, setEditing] = useState(null); // location being edited
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // The branch picker is the same list the Customers screen uses - a pinned
  // role gets exactly one, so there is no choice to offer them.
  useEffect(() => {
    fetch(`${API_BASE}/api/customers/branches`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setBranches(list);
        if (list.length) setBranchId((b) => b || String(list[0].account_id));
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ account_id: branchId });
      if (includeRetired) qs.set("include_inactive", "true");
      const data = await api(`/?${qs.toString()}`, token);
      setLocations(data.locations || []);
      setDrivers(data.drivers || []);
      setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [branchId, includeRetired, token]);

  useEffect(() => { load(); }, [load]);

  // Grouped by owner. Branch shelves first: they are the shared thing, and a
  // driver's totes only make sense once you know what else is in the building.
  const branchLocations = useMemo(
    () => locations.filter((l) => !l.driver_id),
    [locations],
  );
  const byDriver = useMemo(() => {
    const map = new Map();
    for (const d of drivers) map.set(d.driver_id, { driver: d, rows: [] });
    for (const l of locations) {
      if (!l.driver_id) continue;
      // A tote owned by somebody who no longer works at this branch still has
      // to appear, or it becomes invisible and unfixable.
      if (!map.has(l.driver_id)) {
        map.set(l.driver_id, {
          driver: { driver_id: l.driver_id, full_name: l.driver_name, default_location_id: null,
                    orphaned: true },
          rows: [],
        });
      }
      map.get(l.driver_id).rows.push(l);
    }
    return [...map.values()];
  }, [locations, drivers]);

  // Derived rather than stored, so the panel always reflects the CURRENT data.
  // Holding a copy of the driver in state would leave the totes and the
  // default frozen at whatever they were when the name was clicked.
  const selected = useMemo(
    () => byDriver.find((d) => d.driver.driver_id === selectedDriverId) || null,
    [byDriver, selectedDriverId],
  );

  // Land on somebody. An empty right-hand panel on arrival makes the screen
  // look broken, and a selection pointing at a driver from the previous branch
  // is worse - it would show one branch's roster beside another's totes.
  useEffect(() => {
    if (!byDriver.length) { setSelectedDriverId(null); return; }
    if (byDriver.some((d) => d.driver.driver_id === selectedDriverId)) return;
    setSelectedDriverId(byDriver[0].driver.driver_id);
  }, [byDriver, selectedDriverId]);

  const save = async (path, options) => {
    await api(path, token, options);
    await load();
  };

  const setDefault = (driverId, locationId) => save("/default", {
    method: "PUT",
    body: JSON.stringify({ account_id: branchId, driver_id: driverId, location_id: locationId }),
  }).catch((e) => setError(e.message));

  const retire = (l) => {
    const ok = window.confirm(
      `Retire "${l.description}"?\n\nIt stops appearing in pickers. Parcels that `
      + "were on it keep their history, and you can bring it back by showing "
      + "retired locations.",
    );
    if (!ok) return;
    save(`/${l.id}`, { method: "PATCH", body: JSON.stringify({ is_active: false }) })
      .catch((e) => setError(e.message));
  };

  const restore = (l) => save(`/${l.id}`, {
    method: "PATCH", body: JSON.stringify({ is_active: true }),
  }).catch((e) => setError(e.message));

  return (
    <>
    <MessagingPanel />
    <div style={S.wrap}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <h1 style={S.h1}>Locations</h1>
        <select style={{ ...S.input, width: "auto", minWidth: 200 }}
          value={branchId} onChange={(e) => setBranchId(e.target.value)}>
          {branches.map((b) => (
            <option key={b.account_id} value={b.account_id}>
              {displayName(b.name, masked)}
            </option>
          ))}
        </select>
        <label style={{ fontSize: 12.5, display: "flex", alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)} />
          Show retired
        </label>
        <button style={S.btn} onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <div style={{ flex: 1 }} />
        <MaskToggleButton />
      </div>

      {error && <div style={{ ...S.error, marginBottom: 12 }}>{error}</div>}

      {/* ---------------- branch locations ---------------- */}
      <div style={S.panel}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={S.label}>Branch locations</div>
          <div style={{ flex: 1 }} />
          <button style={S.btn} onClick={() => setAdding({ driverId: "" })}>
            Add a branch location
          </button>
        </div>
        <div style={{ ...S.muted, marginBottom: 8 }}>
          Shelves and bays inside the depot. Bags wait here until a driver
          takes them, and every driver at this branch can see them.
        </div>
        <LocationTable
          rows={branchLocations}
          empty="No branch shelves yet. Totes still work without them — this is for bags that wait indoors."
          onEdit={setEditing}
          onRetire={retire}
          onRestore={restore} />
      </div>

      {/* ---------------- drivers: roster left, their totes right ---------- */}
      {byDriver.length === 0 ? (
        <div style={{ ...S.panel, ...S.muted }}>
          No drivers are set up at this branch yet.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* The roster. Fixed-ish width because it is an index, not content -
              letting it share the space equally would give a column of short
              names the same room as a table of boxes. */}
          <div style={{ ...S.panel, flex: "0 1 260px", minWidth: 200 }}>
            <div style={{ ...S.label, marginBottom: 8 }}>Drivers</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {byDriver.map(({ driver, rows }) => {
                const on = driver.driver_id === selectedDriverId;
                const active = rows.filter((l) => l.is_active).length;
                return (
                  <button
                    key={driver.driver_id}
                    onClick={() => setSelectedDriverId(driver.driver_id)}
                    style={{
                      textAlign: "left", cursor: "pointer", width: "100%",
                      border: on ? "2px solid #0f766e" : "1px solid #d1d5db",
                      background: on ? "#f0fdfa" : "#fff",
                      borderRadius: 6, padding: on ? "7px 9px" : "8px 10px",
                      font: "inherit", color: "inherit",
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {displayName(driver.full_name, masked)}
                    </div>
                    <div style={S.muted}>
                      {active} tote{active === 1 ? "" : "s"}
                      {driver.orphaned && " · no longer here"}
                    </div>
                    {/* Flagged on the roster, not only inside the panel: a
                        driver with no default hits a dead end on their first
                        bag of the day, and that is worth seeing without
                        clicking through every name to find it. */}
                    {!driver.default_location_id && !driver.orphaned && (
                      <div style={{ ...S.muted, color: "#b45309" }}>No default set</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* The selected driver's boxes. */}
          <div style={{ ...S.panel, flex: "1 1 420px", minWidth: 0 }}>
            {!selected ? (
              <div style={S.muted}>Pick a driver to see their totes.</div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10,
                              marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {displayName(selected.driver.full_name, masked)}
                  </div>
                  {selected.driver.orphaned && (
                    <span style={S.muted}>no longer at this branch</span>
                  )}
                  <div style={{ flex: 1 }} />
                  {selected.driver.default_location_id && (
                    <button style={S.btn}
                      title="The driver will have to choose a location for every bag until a new default is set"
                      onClick={() => setDefault(selected.driver.driver_id, null)}>
                      Clear default
                    </button>
                  )}
                  <button style={S.btnGo}
                    onClick={() => setAdding({ driverId: selected.driver.driver_id })}>
                    Add a tote
                  </button>
                </div>

                <div style={{ ...S.muted, marginBottom: 8 }}>
                  {selected.driver.default_location_id
                    ? "New bags go into the default tote unless the driver picks another."
                    : "No default set — adding a bag will make this driver choose a "
                      + "location first, every time."}
                </div>

                <LocationTable
                  rows={selected.rows}
                  empty="No totes yet. Add one, or every bag this driver picks up needs a location chosen by hand."
                  defaultLocationId={selected.driver.default_location_id}
                  onMakeDefault={(l) => setDefault(selected.driver.driver_id, l.id)}
                  onEdit={setEditing}
                  onRetire={retire}
                  onRestore={restore} />
              </>
            )}
          </div>
        </div>
      )}

      {adding && (
        <LocationDialog
          title={adding.driverId ? "Add a tote" : "Add a branch location"}
          drivers={drivers}
          masked={masked}
          initial={{
            description: "",
            location_type: adding.driverId ? "tote" : "shelf",
            barcode: "",
            driver_id: adding.driverId || "",
          }}
          onCancel={() => setAdding(null)}
          onSave={async (values) => {
            await save("/", {
              method: "POST",
              body: JSON.stringify({ ...values, account_id: branchId }),
            });
            setAdding(null);
          }} />
      )}

      {editing && (
        <LocationDialog
          title="Edit location"
          drivers={drivers}
          masked={masked}
          initial={{
            description: editing.description || "",
            location_type: editing.location_type,
            barcode: editing.barcode || "",
            driver_id: editing.driver_id || "",
          }}
          warning={editing.is_someones_default
            ? "This is somebody's default. Moving it to another driver clears "
              + "that default, so they will have to pick a location for each bag "
              + "until a new one is set."
            : null}
          onCancel={() => setEditing(null)}
          onSave={async (values) => {
            await save(`/${editing.id}`, { method: "PATCH", body: JSON.stringify(values) });
            setEditing(null);
          }} />
      )}
    </div>
    </>
  );
}

function LocationTable({
  rows, empty, onEdit, onRetire, onRestore,
  defaultLocationId, onMakeDefault,
}) {
  if (!rows.length) return <div style={S.muted}>{empty}</div>;

  // Branch shelves have no default action - a default is where a driver's bags
  // land when they join his round, and a shelf in the depot is where bags are
  // NOT once they are with him. Offering it there would be an option that only
  // ever makes things wrong.
  const canDefault = typeof onMakeDefault === "function";

  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Name</th>
          <th style={S.th}>Type</th>
          <th style={S.th}>Barcode</th>
          <th style={S.th}>Waiting</th>
          <th style={S.th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id} style={{ opacity: l.is_active ? 1 : 0.55 }}>
            <td style={S.td}>
              {l.description}
              {l.is_someones_default && (
                <span style={{ ...S.chip, marginLeft: 8 }}>Default</span>
              )}
              {!l.is_active && <span style={{ ...S.muted, marginLeft: 8 }}>retired</span>}
            </td>
            <td style={S.td}>{l.location_type}</td>
            <td style={S.td}>
              {l.barcode || <span style={S.muted}>—</span>}
            </td>
            <td style={S.td}>
              {/* Zero is worth printing here, unlike in the app: an operator
                  looking at this table is deciding whether a shelf is safe to
                  retire, and blank would read as unknown rather than empty. */}
              {l.waiting_count}
            </td>
            <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
              {canDefault && l.is_active && String(l.id) !== String(defaultLocationId || "") && (
                <button style={{ ...S.btn, marginRight: 6 }}
                  title="New bags go here unless the driver picks another"
                  onClick={() => onMakeDefault(l)}>
                  Make default
                </button>
              )}
              <button style={S.btn} onClick={() => onEdit(l)}>Edit</button>
              {l.is_active ? (
                <button style={{ ...S.btn, marginLeft: 6 }}
                  title={l.waiting_count > 0
                    ? "Parcels are still waiting here — move them first"
                    : "Stop this appearing in pickers"}
                  onClick={() => onRetire(l)}>
                  Retire
                </button>
              ) : (
                <button style={{ ...S.btn, marginLeft: 6 }} onClick={() => onRestore(l)}>
                  Restore
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Add and edit share one dialog. The only thing that differs is which fields
 * start filled, and the owner is a normal field here rather than fixed by
 * which button was pressed - reassigning a tote to another driver, or handing
 * one back to the branch, is the same edit as renaming it.
 */
function LocationDialog({ title, initial, drivers, masked, warning, onCancel, onSave }) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setValues((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!values.description.trim()) { setError("A name is required"); return; }
    setBusy(true); setError(null);
    try {
      await onSave({
        description: values.description.trim(),
        location_type: values.location_type,
        // "" means cleared, and the API tells absent from cleared - so an
        // empty barcode field genuinely removes the barcode rather than
        // leaving the old one in place.
        barcode: values.barcode.trim() || null,
        driver_id: values.driver_id || null,
      });
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 3000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}
      onClick={onCancel}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: 18, width: 440, maxWidth: "100%",
        fontFamily: "system-ui, sans-serif",
      }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{title}</div>

        {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
        {warning && (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                        color: "#78350f", padding: 8, borderRadius: 6,
                        fontSize: 12, marginBottom: 10 }}>
            {warning}
          </div>
        )}

        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <div style={S.label}>Name</div>
            <input style={S.input} autoFocus value={values.description}
              placeholder="e.g. Tote 3 — back of van"
              onChange={(e) => set("description", e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={S.label}>Type</div>
              <select style={S.input} value={values.location_type}
                onChange={(e) => set("location_type", e.target.value)}>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={S.label}>Barcode (optional)</div>
              <input style={S.input} value={values.barcode}
                onChange={(e) => set("barcode", e.target.value)} />
            </div>
          </div>

          <div>
            <div style={S.label}>Belongs to</div>
            <select style={S.input} value={values.driver_id}
              onChange={(e) => set("driver_id", e.target.value)}>
              <option value="">The branch — everyone here can use it</option>
              {drivers.map((d) => (
                <option key={d.driver_id} value={d.driver_id}>
                  {displayName(d.full_name, masked)}
                </option>
              ))}
            </select>
            <div style={{ ...S.muted, marginTop: 3 }}>
              A tote that belongs to a driver appears only in their app.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button style={S.btn} disabled={busy} onClick={onCancel}>Cancel</button>
          <button style={S.btnGo} disabled={busy} onClick={submit}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
